import { Dialog } from '@angular/cdk/dialog';
import { CdkMenuModule } from '@angular/cdk/menu';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, model, signal, Type } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { TwAddIcon, TwFolderIcon } from '@foundation/icons';
import { RequestService } from '@foundation/network/services';
import { AccessShareModalComponent } from '@foundation/shared/access';
import { OctetHumanReadablePipe } from '@foundation/utils';
import { combineLatest, map, of, switchMap, tap } from 'rxjs';
import { EXPLORER_RESOURCE_DEFINITIONS, EXPLORER_ROOT_VIRTUAL_FOLDERS, ExplorerResourceDefinition } from '../explorer.types';
import { ExplorerPreviewFallbackComponent } from '../explorer-preview-fallback/explorer-preview-fallback.component';
import { TranslateDirective, TranslatePipe } from '@foundation/translations/services';

@Component({
	selector: 'lib-explorer',
	standalone: true,
	imports: [CommonModule, RouterModule, CdkMenuModule, TwFolderIcon, TwAddIcon, TranslateDirective, TranslatePipe, OctetHumanReadablePipe],
	templateUrl: './explorer.component.html',
	styleUrl: './explorer.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExplorerComponent {
	private _requestService = inject(RequestService);
	private _sanitizer = inject(DomSanitizer);
	private _dialog = inject(Dialog);
	private _definitions = inject(EXPLORER_RESOURCE_DEFINITIONS, { optional: true }) || [];
	private _virtualFolders = inject(EXPLORER_ROOT_VIRTUAL_FOLDERS, { optional: true }) || [];

	folderId = model<string | null>(null);

	// Data for the view
	currentFolder = signal<{ id: string; name?: string } | null>(null);
	subfolders = signal<{ id: string; name: string; link?: any[]; iconComponent?: any }[]>([]);
	resources = signal<any[]>([]);

	// Preview panel
	selectedResource = signal<any>(null);
	selectedPreviewComponent = computed<Type<any> | null>(() => {
		const resource = this.selectedResource();
		if (!resource) return null;
		const def = this.getDefinition(resource._explorerKind);
		return def?.previewComponent ?? ExplorerPreviewFallbackComponent;
	});

	// Detect if any definition has these optional callbacks to show/hide columns
	hasTypeBadgeColumn = this._definitions.some((d) => d.getTypeBadge);
	hasSizeColumn = this._definitions.some((d) => d.getSize);
	hasDateColumn = this._definitions.some((d) => d.getDate);

	// Helper map for quick lookup
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private _definitionsMap = new Map<string, ExplorerResourceDefinition<any>>();

	constructor() {
		this._definitions.forEach((d) => this._definitionsMap.set(d.kind, d));

		effect(() => {
			const folderId = this.folderId();
			if (folderId) {
				this.fetchFolderContent(folderId);
			} else {
				this.fetchRootContent();
			}
		});
	}

	fetchRootContent() {
		this._requestService
			.getBasic$<{
				data: { id: string; name: string }[];
			}>('/api/folders', {
				filters: 'parent_id:~null:exact',
				page_size: 100,
			})
			.pipe(
				tap((response) => {
					this.currentFolder.set({ id: '', name: 'Root' });

					this.subfolders.set([...this._virtualFolders, ...(response.result?.data || [])]);
					this.resources.set([]);
				})
			)
			.subscribe();
	}

	fetchFolderContent(folderId: string) {
		this._requestService
			.getBasic$<{
				folderId: string;
				subfoldersAndResources: {
					id: string;
					name?: string;
					children: { id: string; name: string }[];
					resources: { id: string; kind: string }[];
				}[];
			}>('/api/folders/' + folderId + '/subfolders')
			.pipe(
				switchMap((response) => {
					if (response.result && response.result.subfoldersAndResources.length > 0) {
						const data = response.result.subfoldersAndResources.find((f) => f.id === folderId) || response.result.subfoldersAndResources[0];

						if (data) {
							this.currentFolder.set({ id: data.id, name: data.name });
							this.subfolders.set(data.children || []);

							if (!data.resources || data.resources.length === 0) {
								this.resources.set([]);
								return of([]);
							}

							// Hydrate resources
							const resourceObservables = data.resources.map((r) => {
								const def = this._definitionsMap.get(r.kind);
								if (def && def.load) {
									return def.load(r.id).pipe(map((obj) => ({ ...obj, _explorerKind: r.kind })));
								}
								return of({ ...r, _explorerKind: r.kind });
							});

							return combineLatest(resourceObservables);
						}
					}
					return of([]);
				}),
				tap((resources: any[]) => {
					this.resources.set(resources);
				})
			)
			.subscribe();
	}

	getDefinition(kind: string): ExplorerResourceDefinition | undefined {
		return this._definitionsMap.get(kind);
	}

	getIcon(resource: any): SafeHtml {
		const def = this.getDefinition(resource._explorerKind);
		const iconHtml = def?.getIcon ? def.getIcon(resource) : '';
		if (!iconHtml) return '';
		// If it's an emoji (simple length check or regex, but here assuming SVG string if long)
		if (iconHtml.length < 10) return iconHtml;

		return this._sanitizer.bypassSecurityTrustHtml(iconHtml);
	}

	getIconComponent(resource: any): any {
		const def = this.getDefinition(resource._explorerKind);
		return def?.iconComponent;
	}

	getName(resource: any): string {
		const def = this.getDefinition(resource._explorerKind);
		if (def?.getName) {
			return def.getName(resource);
		}
		return resource.name || resource.title || resource.id;
	}

	getViewLink(resource: any): any[] | null {
		const def = this.getDefinition(resource._explorerKind);
		if (def?.viewLink) {
			return def.viewLink(resource);
		}
		return null;
	}

	getTypeBadge(resource: any): string | null {
		const def = this.getDefinition(resource._explorerKind);
		return def?.getTypeBadge ? def.getTypeBadge(resource) : null;
	}

	getSize(resource: any): number | null {
		const def = this.getDefinition(resource._explorerKind);
		return def?.getSize ? def.getSize(resource) : null;
	}

	getDate(resource: any): string | null {
		const def = this.getDefinition(resource._explorerKind);
		return def?.getDate ? def.getDate(resource) : null;
	}

	selectResource(resource: any) {
		const current = this.selectedResource();
		if (current?.id === resource.id && current?._explorerKind === resource._explorerKind) {
			this.selectedResource.set(null);
		} else {
			this.selectedResource.set(resource);
		}
	}

	closePreview() {
		this.selectedResource.set(null);
	}

	handleCreateAction(action: any) {
		const folderId = this.folderId();
		const result = action.onClick(folderId);

		if (result && typeof result.subscribe === 'function') {
			result
				.pipe(
					tap(() => {
						if (folderId) {
							this.fetchFolderContent(folderId);
						} else {
							this.fetchRootContent();
						}
					})
				)
				.subscribe();
		}
	}

	public shareFolder(folder: { id: string; name: string }) {
		this._dialog.open(AccessShareModalComponent, {
			data: {
				resourceId: folder.id,
				resourceKind: 'folder',
			},
			width: 'auto',
			height: 'auto',
			maxWidth: '1080px',
			maxHeight: '95%',
			panelClass: 'overflow-auto',
		});
	}

	get createActions() {
		return this._definitions
			.filter((d) => d.createAction)
			.map((d) => {
				const action = d.createAction;
				if (!action)
					return {
						label: '',
						onClick: () => {
							// no action
						},
						iconSafe: null,
					};
				return {
					...action,
					iconSafe: action.icon ? this._sanitizer.bypassSecurityTrustHtml(action.icon) : null,
				};
			})
			.sort((a, b) => a.label.localeCompare(b.label));
	}
}
